import { useIsMutating } from "@tanstack/react-query";
import {
  AnimationComponent,
  ArrowLeftIcon,
  ArrowRightIcon,
  CircularProgress,
  Divider,
  GKButton,
  Grid2,
  Show,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DetailsTab from "Pages/InvestorOnboarding/DetailsTab";
import { useInvestorContext } from "Pages/InvestorOnboarding/InvestorContext";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";

const MfOnboarding = () => {
  const { dispatch } = useInvestorContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientName = searchParams.get("client_name");
  const clientEmail = searchParams.get("client_email");
  const phone = searchParams.get("phone");
  const basketId = searchParams.get("basket_id");
  const companyId = searchParams.get("company_id");
  const feePlanId = searchParams.get("fee_plan_id");
  const partnerId = searchParams.get("partner_id");
  const clientId = searchParams.get("client_id");
  const token = searchParams.get("token");

  const form = useForm({
    defaultValues: {
      activeStep: 0,
      activeStepText: "DETAILS",
      fetchedFromKra: false,
      clientObject: null,
      retakeAssessment: true,
      selectedFeePlan: {},
      planDataObject: {
        aumCash: "",
        showHoldingsModal: false,
        aumBasedPlan: "",
        subscriptionPlan: null,
        subscriptionPlanAccountType: null,
        holdingsProofs: [],
        cashProofs: [],
        plansTimeperiod: "",
        plansHoldings: [
          {
            gridCode: "",
            quantity: 0,
            cmp: "",
            id: 0,
          },
        ],
      },
      clientCreation: {
        personalDetails: {
          pan: "",
          dateOfBirth: dayjs().format("YYYY-MM-DD"),
          name: clientName || "",
          email: clientEmail || "",
          aadhar: "",
          gender: "",
          maritalStatus: "",
          fatherName: "",
          motherName: "",
          country_of_birth: "",
          place_of_birth: "",
          nationality_country: "",
          income: "",
          occupation: "",
          source_of_wealth: "",
          address: "",
          country: "",
          state: "",
          city: "",
          pincode: "",
        },
        bankAccounts: [
          {
            name: "",
            accountNum: "",
            confirmAccountNum: "",
            type: "",
            ifsc: "",
          },
        ],
        dematAccounts: [],
        nominees: [],
        phone: phone || "",
        countryCode: "+91",
        basketId: +basketId || null,
        company: +companyId || null,
        feePlan: feePlanId || null,
        partner: +partnerId || null,
      },
    },
  });

  useFetchQuery({
    key: ["GET_CLIENT_BY_ID_ONBOARDING"],
    route: clientId && clientId !== "null" ? `/client/${clientId}/` : "",
    enabled: !!clientId && clientId !== "null",
    success: (response: any) => {
      form.setValue("clientCreation.personalDetails", {
        ...response?.data?.personalDetails,
        country: "",
        state: "",
        city: "",
      });
      form.setValue(
        "clientCreation.bankAccounts",
        response?.data?.bankAccounts,
      );
      form.setValue(
        "clientCreation.dematAccounts",
        response?.data?.dematAccounts,
      );
      form.setValue("clientCreation.nominees", response?.data?.nominees);
    },
  });

  const activeStepWatch = form.watch("activeStep");

  const handleNext = () => form.setValue("activeStep", activeStepWatch + 1);
  const handlePrevious = () => form.setValue("activeStep", activeStepWatch - 1);

  const { isFetching } = useFetchQuery({
    key: ["GET_CLIENT_FOR_CREATION"],
    route: `/client-onboard/?pan=${form.getValues("clientCreation.personalDetails.pan")}&phone=${form.getValues("clientCreation.phone")}&basket_id=${basketId}&company=${companyId}`,
    enabled: activeStepWatch >= 1,
    success: (response: any) => {
      form.setValue("clientObject", response?.data?.data);
      // The created/fetched client carries its own id; the URL `client_id`
      // can be empty in the OTP onboarding flow, so prefer the response id.
      const resolvedClientId = response?.data?.data?.id ?? clientId;
      form.setValue(
        "selectedFeePlan",
        response?.data?.data?.basket?.feePlansList?.find(
          (dataItem: { id: string }) => dataItem?.id === feePlanId,
        ),
      );
      form.setValue(
        "retakeAssessment",
        !response?.data?.data?.riskprofileDetails?.points,
      );

      axiosInstance.interceptors.request.use((config) => {
        config.headers.Authorization = `Token ${response?.data?.data?.token}`;
        config.headers[`x-gridkey-user-role`] = `client`;
        return config;
      });

      dispatch({ type: "TOKEN", data: response?.data?.data?.token });
      dispatch({ type: "IS_EXIST_TOKEN", data: true });

      // Fetch client data again to get fpOnboardingSession for navigation
      axiosInstance
        .get(`/client/${resolvedClientId}/`)
        .then((clientResponse: any) => {
          const fpOnboardingSession = clientResponse?.data?.fpOnboardingSession;

          const fpPreVerificationStatus =
            fpOnboardingSession?.fpPreVerificationStatus;

          const fpReadinessStatus = fpOnboardingSession?.fpReadinessStatus;

          const successStatuses = ["completed", "accepted", "approved"];
          const failedStatuses = ["failed", "Unavailable", "Incomplete"];

          // Step 1: Check fpPreVerificationStatus
          if (successStatuses.includes(fpPreVerificationStatus)) {
            // Step 2: Check fpReadinessStatus
            if (successStatuses.includes(fpReadinessStatus)) {
              // Both success -> Complete page
              navigate(
                `/investor/mf/onboarding/complete?client_name=${clientName}&client_email=${clientEmail}&phone=${phone}&basket_id=${basketId}&company_id=${companyId}&fee_plan_id=${feePlanId}&partner_id=${partnerId}&client_id=${resolvedClientId}&token=${token}`,
              );
            } else if (failedStatuses.includes(fpReadinessStatus)) {
              // Readiness failed -> Status page
              navigate(
                `/investor/mf/onboarding/status?client_name=${clientName}&client_email=${clientEmail}&phone=${phone}&basket_id=${basketId}&company_id=${companyId}&fee_plan_id=${feePlanId}&partner_id=${partnerId}&client_id=${resolvedClientId}&token=${token}&client_code=${response?.data?.data?.code || ""}&basket_name=${
                  response?.data?.data?.basket?.name || ""
                }&fp_readiness_status=${encodeURIComponent(fpReadinessStatus || "")}`,
              );
            }
          } else if (failedStatuses.includes(fpPreVerificationStatus)) {
            // PreVerification failed -> Status page
            navigate(
              `/investor/mf/onboarding/status?client_name=${clientName}&client_email=${clientEmail}&phone=${phone}&basket_id=${basketId}&company_id=${companyId}&fee_plan_id=${feePlanId}&partner_id=${partnerId}&client_id=${resolvedClientId}&token=${token}&client_code=${response?.data?.data?.code || ""}&basket_name=${
                response?.data?.data?.basket?.name || ""
              }&fp_readiness_status=${encodeURIComponent(fpReadinessStatus || "")}`,
            );
          } else {
            // Fallback: If statuses don't match expected conditions, navigate to status page
            navigate(
              `/investor/mf/onboarding/status?client_name=${clientName}&client_email=${clientEmail}&phone=${phone}&basket_id=${basketId}&company_id=${companyId}&fee_plan_id=${feePlanId}&partner_id=${partnerId}&client_id=${resolvedClientId}&token=${token}&client_code=${response?.data?.data?.code || ""}&basket_name=${
                response?.data?.data?.basket?.name || ""
              }&fp_readiness_status=${encodeURIComponent(fpReadinessStatus || "")}`,
            );
          }
        });
    },
  });

  const { mutate: handleClientCreation } = useMutation({
    mutationKey: ["CLIENT_CREATION"],
    mutationFn: (data) => axiosInstance.post("/client-onboard/", data),
    onSuccess: () => {
      refetchQuery("GET_CLIENT_FOR_CREATION");
      handleNext();
    },
  });

  const tabs = [
    {
      name: "Details",
      isCompleted: activeStepWatch !== 0,
      disabled: activeStepWatch !== 0,
      show: true,
      component: <DetailsTab companyId={companyId} basketId={basketId} />,
      value: "DETAILS",
      id: "details",
    },
  ];

  const filteredTabs = tabs;
  const activeTabValue = filteredTabs[activeStepWatch]?.value;

  const onSubmit = (values: any) => {
    const currentTabValue = filteredTabs[activeStepWatch]?.value;

    if (currentTabValue === "DETAILS") {
      return handleClientCreation(values.clientCreation);
    }

    return undefined;
  };

  const clientCreationLoading = !!useIsMutating({
    mutationKey: ["CLIENT_CREATION"],
  });

  return (
    <FormProvider {...form}>
      <form
        style={{ height: "100%", width: "100%", background: "#fff" }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Grid2
          container
          spacing={0}
          bgcolor={"var(--white)"}
          justifyContent={"center"}
        >
          <Grid2 size={12} container justifyContent={"center"}>
            <Grid2 size={6} />
            <Show.When isTrue={form.getValues("activeStep") >= 1}>
              <Divider sx={{ width: "100%" }} />
            </Show.When>
          </Grid2>
          <Grid2
            container
            overflow={"hidden auto"}
            p={2}
            sx={{ maxWidth: { md: "580px", xs: "100%" }, width: "100%" }}
          >
            {/* <Grid2 size={12} display={"flex"} flexDirection={"column"} gaitsp={1}>
              <Typography>Hello</Typography>
              <Typography>adakskdhshdsah</Typography>
            </Grid2> */}
            {filteredTabs?.map((tab, index) => (
              <Show.When key={index} isTrue={activeStepWatch === index}>
                <Grid2 size={12} py={5} sx={{ width: "100%" }}>
                  <AnimationComponent>{tab.component}</AnimationComponent>
                </Grid2>
              </Show.When>
            ))}
          </Grid2>
          <Grid2
            container
            position={"sticky"}
            bottom={0}
            bgcolor={"#fff"}
            size={12}
            zIndex={99}
            display={"flex"}
            justifyContent={"center"}
            gap={1}
            alignItems={"center"}
            p={2}
            borderTop={"1px solid var(--neutral-100)"}
          >
            <Grid2
              size={{ xs: 12, md: 6 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              sx={{ maxWidth: "580px", width: "100%" }}
            >
              <GKButton
                onClick={() => {
                  handlePrevious();
                }}
                startIcon={<ArrowLeftIcon />}
              >
                Back
              </GKButton>

              {activeTabValue === "AGREEMENT" && (
                <GKButton
                  variant="outlined"
                  color="dark"
                  sx={{ ml: "auto" }}
                  target="_blank"
                  href={form.getValues("clientObject.agreementTemplate")}
                >
                  Read Agreement &nbsp;
                </GKButton>
              )}

              {!["PAYMENT", "COMPLETED"].includes(activeTabValue) && (
                <GKButton
                  variant="contained"
                  type="submit"
                  loading={clientCreationLoading || isFetching}
                  endIcon={<ArrowRightIcon />}
                  // disabled={
                  //   (activeTabValue === "KYC" &&
                  //     !form.getValues("clientObject.kycDetails.kycId")) ||
                  //   (activeTabValue === "AGREEMENT" &&
                  //     !form.getValues(
                  //       "clientObject.agreementDetails.agreementStatus.id",
                  //     ))
                  // }
                >
                  {activeTabValue === "UPLOAD_PROOF" &&
                  form.watch("planDataObject.holdingsProofs").length === 0 &&
                  form.watch("planDataObject.cashProofs").length === 0
                    ? "Skip & Next"
                    : "Next"}
                  &nbsp;
                </GKButton>
              )}
            </Grid2>
          </Grid2>
        </Grid2>

        {/* Centered Loader Overlay */}
        <Show.When isTrue={clientCreationLoading || isFetching}>
          <Grid2
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255, 255, 255, 0.8)"
            display="flex"
            justifyContent="center"
            alignItems="center"
            zIndex={9999}
          >
            <CircularProgress size={40} />
          </Grid2>
        </Show.When>
      </form>
    </FormProvider>
  );
};

export default MfOnboarding;
